import { cn } from "@/lib/utils";

/**
 * Compact letter-tile avatar used by the chat transcript and the
 * sessions rail. Pure CSS — no images. The colour is a
 * deterministic hash of the seed so the same agent / user always
 * gets the same tile across reloads.
 */

const PALETTE = [
  ["bg-amber-500/85", "text-white"],
  ["bg-orange-500/85", "text-white"],
  ["bg-rose-500/85", "text-white"],
  ["bg-fuchsia-500/85", "text-white"],
  ["bg-violet-500/85", "text-white"],
  ["bg-indigo-500/85", "text-white"],
  ["bg-sky-500/85", "text-white"],
  ["bg-teal-500/85", "text-white"],
  ["bg-emerald-500/85", "text-white"],
  ["bg-lime-600/85", "text-white"],
] as const;

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function avatarColor(seed: string): string {
  if (!seed) return "bg-muted";
  return PALETTE[hash(seed) % PALETTE.length].join(" ");
}

export interface AvatarProps {
  seed: string;
  label?: string;
  size?: "xs" | "sm" | "md";
  className?: string;
}

export function LetterAvatar({ seed, label, size = "md", className }: AvatarProps) {
  const dim =
    size === "xs"
      ? "h-4 w-4 text-[9px] rounded-[3px]"
      : size === "sm"
        ? "h-5 w-5 text-[10px] rounded-[4px]"
        : "h-7 w-7 text-[12px] rounded-md";
  const letter = (label ?? seed).trim().charAt(0).toUpperCase() || "?";
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex shrink-0 items-center justify-center font-semibold tracking-tight",
        avatarColor(seed),
        dim,
        className,
      )}
    >
      {letter}
    </span>
  );
}
